import React, { useState, useEffect } from "react";
import ToggleSwitch from "../Buttons/Togglebtn";
import { Pencil, Trash } from "lucide-react";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { statusupdatemealdetail } from "../../redux/slices/gallerySlice";
import { UpdateProviderStatus } from "../../redux/slices/ProvidergalSlice";

const GalCard = ({
  mealId,
  providerId,
  status,
  cardImage,
  cardCategory = "category",
  cardTitle = "Card Title",
  countryName = "country",
  // MAIN TOGGLE (with status) - remains purely UI
  showMainToggle = true,
  providerStatus = "active",
  mainToggleProps = {},

  // BUTTONS
  showButtons = true,
  onEdit,
  onDelete,
  editBtnText = "Edit",
  deleteBtnText = "Delete",
  editIcon = <Pencil size={16} />,
  deleteIcon = <Trash size={16} />,

  // SMALL TOGGLE (footer, now updates status)
  showSmallToggle = false,
  smallToggleProps = {},
}) => {
  const [isSmallToggleOn, setIsSmallToggleOn] = useState(status === 1); // Initialize from status
  const [isBigToggleOn, setIsBigToggleOn] = useState(status === 1); // purely UI
  const [currentproviderStatus, setCurrentproviderStatus] = useState(
    status === 1 ? "Active" : "Inactive"
  );
  const dispatch = useDispatch();

  useEffect(() => {
    setIsSmallToggleOn(status === 1);
  }, [status]);

  useEffect(() => {
    setIsBigToggleOn(status === 1);
    setCurrentproviderStatus(status === 1 ? "Active" : "Inactive");
  }, [status]);

  // Big toggle - same as original, no status API call
  const handleBigToggle = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will change the status!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    }).then((result) => {
      if (result.isConfirmed) {
        // Toggle ke liye current status ka opposite bhejo
        const toggleStatus = isBigToggleOn ? 0 : 1;

        dispatch(
          UpdateProviderStatus({
            provider_image_gallery_id: providerId,
            is_confirmed: toggleStatus,
          })
        )
          .unwrap()
          .then((res) => {
            console.log("Toggle response:", res);

            // API response me 'new_status' milega
            const newStatus = res.is_confirmed === 1;

            // UI update
            setIsBigToggleOn(newStatus);
            setCurrentproviderStatus(newStatus ? "Active" : "Inactive");

            Swal.fire({
              title: "Updated!",
              text: "Status changed successfully.",
              icon: "success",
            });
          })
          .catch((err) => {
            console.error("Toggle error:", err);
            Swal.fire({
              title: "Error!",
              text: "Unable to update toggle status",
              icon: "error",
            });
          });
      }
    });
  };

  // Small toggle - updates status via API
  const handleSmallToggle = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will change the status!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(statusupdatemealdetail({ meal_image_gallery_id: mealId }))
          .unwrap()
          .then((res) => {
            setIsSmallToggleOn(res.status === 1);
            Swal.fire({
              title: "Updated!",
              text: "Status changed successfully.",
              icon: "success",
            });
          })
          .catch(() => {
            Swal.fire({
              title: "Error!",
              text: "Unable to update toggle status",
              icon: "error",
            });
          });
      }
    });
  };

  return (
    <div className="bg-white w-full border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow duration-300">
      <div className="flex justify-between mb-4 border-b border-gray-100">
        {/* Only show category if not using main toggle */}
        {!showMainToggle && (
          <p className="text-start text-black mb-2 text-base">{cardCategory}</p>
        )}

        {/* Country name always displayed */}
        <p className="text-base mb-2">
          {countryName.charAt(0).toUpperCase() +
            countryName.slice(1).toLowerCase()}
        </p>
      </div>

      {/* Image */}
      <div className="w-full h-48 mb-3">
        <img
          src={cardImage}
          alt="cardImage"
          className="w-full h-full rounded-lg object-cover"
        />
      </div>

      {/* Title */}
      <p className="text-sm font-medium text-gray-800 px-1 py-2">{cardTitle}</p>

      {/* MAIN TOGGLE */}
      {showMainToggle && (
        <div className="py-2 border-t border-gray-100 flex justify-between">
          <ToggleSwitch
            {...mainToggleProps}
            checked={isBigToggleOn}
            showStatus={false}
            onChange={handleBigToggle}
            onColor="#b4f6c4"
            offColor="#ffb3b3"
            handleColor="#ffffff"
            width={38}
            height={18}
            handleDiameter={18}
          />

          <p
            className={`text-sm font-semibold ${
              currentproviderStatus === "Active"
                ? "text-green-600"
                : "text-red-600"
            }`}
          >
            {currentproviderStatus}
          </p>
        </div>
      )}

      {/* Buttons or small toggle */}
      <div className="flex justify-center items-center gap-4 border-t border-gray-100 pt-4">
        {showButtons && (
          <>
            <button
              onClick={onEdit}
              className="flex items-center cursor-pointer justify-center gap-1 w-full border border-gray-200 bg-white text-gray-700 text-xs py-2 rounded-md hover:bg-gray-200 transition-all"
            >
              {editIcon}
              {editBtnText}
            </button>

            <button
              onClick={onDelete}
              className="flex items-center cursor-pointer justify-center gap-1 w-full border border-red-200 bg-white text-red-600 text-xs py-2 rounded-md hover:bg-red-200 transition-all"
            >
              {deleteIcon}
              {deleteBtnText}
            </button>
          </>
        )}

        {showSmallToggle && (
          <div className="w-full">
            <ToggleSwitch
              checked={isSmallToggleOn}
              onChange={handleSmallToggle} // <-- updates status
              showStatus={false}
              onColor="#b4f6c4"
              offColor="#ffb3b3"
              handleColor="#ffffff"
              width={40}
              height={20}
              handleDiameter={18}
              {...smallToggleProps}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GalCard;
